// Inside the house: rooms she builds with gems, each a bare grid she fills with things she
// earned. Walls and floors can be painted with themes bought once and used anywhere.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { drawSprite } from '@avatar/render';
import { DECOR_BY_ID, THEMES, THEME_BY_ID, type Theme } from '../decor';
import { CHARMS, drawCharm } from '../charms';
import { getSave, update, subscribe } from '../state';
import { topBar, toast } from '../ui';
import { go } from '../router';
import { activePet, drawPet } from '../pets';
import { drawArtContain, onArtReady } from '@kit/assets';
import { ROOM_DEFS, ROOM_DEF_BY_ID, hasRoom, canBuild, newRoom, allPlacedIds, houseStories, type RoomState } from '../house';

export { houseStories } from '../house';

const COLS = 8, ROWS = 6, WALL_ROWS = 2;

export function houseScreen(root: HTMLElement) {
  const bar = topBar('My House', { back: 'hub' });
  const plan = h('div', { style: { padding: '0 16px 6px' } });
  const stage = h('div', { style: { padding: '0 12px' } });
  const msg = h('div.msg');
  const paintPanel = h('div', { style: { padding: '0 16px', display: 'none' } });
  const tray = h('div.row.wrap', { style: { padding: '0 16px', gap: '8px' } });
  const gardenStage = h('div', { style: { padding: '0 12px' } });
  const paintBtn = h('button.btn', { onclick: () => { painting = !painting; renderPaint(); } }, 'Paint');
  const actions = h('div.grid2.pad', null,
    h('button.btn', { onclick: () => go('shop') }, 'Shop for things'),
    paintBtn,
    h('button.btn', { onclick: () => go('pets') }, nestLabel()),
    h('button.btn', { onclick: () => go('bookshelf') }, shelfLabel()),
    h('button.btn', { onclick: () => go('charms') }, `Charms (${getSave().charms.length})`),
    h('button.btn.ghost', { onclick: () => go('hub') }, 'Go outside'),
  );
  function shelfLabel(): string {
    const n = (getSave().kept.story || []).length;
    return n ? `Bookshelf (${n})` : 'Bookshelf';
  }
  function nestLabel(): string {
    const p = getSave().pets;
    const ready = p.eggs.filter(e => e.roundsDone >= e.roundsNeeded).length;
    if (ready) return `The Nest (${ready} ready to hatch!)`;
    if (p.eggs.length) return `The Nest (${p.eggs.length} egg${p.eggs.length > 1 ? 's' : ''})`;
    return p.pets.length ? `The Nest (${p.pets.length} pet${p.pets.length > 1 ? 's' : ''})` : 'The Nest';
  }
  root.append(bar, plan, stage, msg, paintPanel, tray, gardenStage, actions);

  let selected: string | null = null;     // decor id picked from the tray
  let painting = false;
  let pending: { id: string; price: number; label: string; t: number } | null = null; // two-tap purchase
  let tile = 40;
  const S = makeSurface(stage, { aspect: (ROWS + WALL_ROWS + 1.2) / COLS, draw: false, onResize: () => { tile = S.w / COLS; draw(); } });

  function room(): RoomState {
    const hs = getSave().house;
    return hs.rooms[Math.min(hs.current, hs.rooms.length - 1)];
  }
  function setTitle() {
    const t = bar.querySelector('.title') as HTMLElement | null;
    if (t) t.textContent = getSave().house.rooms.length > 1 ? `My House · ${room().name}` : 'My House';
  }

  // ---------- buying (two taps so a small finger cannot spend by accident) ----------
  function askToBuy(id: string, price: number, label: string, then: () => void): boolean {
    const s = getSave();
    if (pending && pending.id === id && Date.now() - pending.t < 6000) {
      if (s.gems < price) { toast(`You need ${price - s.gems} more gems for the ${label}.`); pending = null; return false; }
      update(x => { x.gems -= price; x.totals.gemsSpent += price; });
      then();
      pending = null;
      toast(`${label}: yours!`);
      return true;
    }
    pending = { id, price, label, t: Date.now() };
    msg.textContent = s.gems >= price ? `Tap again to get the ${label} for ${price} gems.` : `The ${label} costs ${price} gems. You have ${s.gems}.`;
    setTimeout(() => { if (pending && pending.id === id && Date.now() - pending.t >= 6000) { pending = null; renderPlan(); renderPaint(); } }, 6200);
    return false;
  }

  // ---------- floor plan strip ----------
  function renderPlan() {
    clear(plan);
    const s = getSave();
    const hs = s.house;
    const rowFor = (story: 1 | 2) => {
      const row = h('div.row.wrap', { style: { gap: '6px', alignItems: 'stretch' } });
      for (const def of ROOM_DEFS.filter(d => d.story === story)) {
        const idx = hs.rooms.findIndex(r => r.id === def.id);
        const owned = idx >= 0;
        const isCurrent = owned && idx === hs.current;
        const buildable = !owned && canBuild(hs, def.id);
        const isPending = pending?.id === `room.${def.id}`;
        const label = owned ? def.name : def.id === 'landing' ? 'Second story' : def.name;
        const b = h('button.btn.small', {
          style: {
            flex: '1 1 auto', minHeight: '44px', flexDirection: 'column', gap: '0', lineHeight: '1.15',
            borderStyle: owned ? 'solid' : 'dashed',
            background: isCurrent ? 'var(--text)' : isPending ? 'var(--accent)' : owned ? 'var(--surface)' : 'transparent',
            color: isCurrent ? 'var(--bg)' : isPending ? 'var(--accent-ink)' : owned ? 'var(--text)' : 'var(--text-2)',
            opacity: !owned && !buildable ? '.55' : '1',
          },
          onclick: () => {
            if (owned) { update(x => { x.house.current = idx; }); selected = null; return; }
            if (!buildable) { msg.textContent = `Build the ${ROOM_DEFS.find(d => canBuild(hs, d.id))?.name ?? 'next room'} first.`; return; }
            askToBuy(`room.${def.id}`, def.price, def.id === 'landing' ? 'second story' : def.name, () => {
              update(x => { x.house.rooms.push(newRoom(def)); x.house.current = x.house.rooms.length - 1; });
            });
            renderPlan();
          },
        }, h('span', null, label), owned ? null : h('span.small', { style: { fontWeight: '700' } }, isPending ? 'Tap again' : `${def.price} gems`));
        row.append(b);
      }
      return row;
    };
    const twoStories = houseStories(s) === 2;
    if (twoStories || canBuild(hs, 'landing')) {
      plan.append(h('div.muted.small', { style: { margin: '2px 0' } }, 'Upstairs'), rowFor(2));
    }
    plan.append(h('div.muted.small', { style: { margin: '6px 0 2px' } }, twoStories || canBuild(hs, 'landing') ? 'Downstairs' : 'Rooms'), rowFor(1));
    setTitle();
  }

  // ---------- paint picker ----------
  function ownsTheme(t: Theme, s = getSave()): boolean { return t.price === 0 || s.owned.includes(t.id); }

  function renderPaint() {
    paintPanel.style.display = painting ? '' : 'none';
    paintBtn.classList.toggle('primary', painting);
    clear(paintPanel);
    if (!painting) return;
    const s = getSave();
    const r = room();
    const section = (kind: 'wall' | 'floor', title: string) => {
      const grid = h('div.row.wrap', { style: { gap: '8px' } });
      for (const t of THEMES.filter(x => x.kind === kind)) {
        const owned = ownsTheme(t, s);
        const on = (kind === 'wall' ? r.wall : r.floor) === t.id;
        const isPending = pending?.id === t.id;
        grid.append(h('button.btn.small', {
          style: { minHeight: '44px', gap: '8px', borderColor: on ? 'var(--accent)' : isPending ? 'var(--accent)' : '', boxShadow: on ? '0 0 0 2px var(--accent) inset' : '', borderStyle: owned ? 'solid' : 'dashed' },
          onclick: () => {
            const apply = () => update(x => { const rr = x.house.rooms[x.house.current]; if (kind === 'wall') rr.wall = t.id; else rr.floor = t.id; });
            if (owned) { apply(); msg.textContent = `${t.name} ${kind}.`; }
            else askToBuy(t.id, t.price, `${t.name} ${kind}`, () => { update(x => { if (!x.owned.includes(t.id)) x.owned.push(t.id); }); apply(); });
            renderPaint();
          },
        }, h('span', { style: { width: '18px', height: '18px', borderRadius: '4px', background: t.swatch, border: '1px solid rgba(0,0,0,.15)', display: 'inline-block' } }), h('span', null, t.name), owned ? null : h('span.small.muted', null, isPending ? 'tap again' : `${t.price}`)));
      }
      return h('div', { style: { marginBottom: '8px' } }, h('div.muted.small', { style: { margin: '4px 0' } }, title), grid);
    };
    paintPanel.append(h('div.card', null, section('wall', 'Walls'), section('floor', 'Floors'), h('p.muted.small', null, 'Paint is bought once and works in every room.')));
  }

  // ---------- decor tray ----------
  function renderTray() {
    clear(tray);
    const s = getSave();
    const placedAnywhere = allPlacedIds(s.house);
    const avail = s.owned.filter(id => id.startsWith('decor.') && !placedAnywhere.has(id));
    const r = room();
    if (pending) { /* keep the purchase prompt */ }
    else if (!avail.length && !r.placed.length) {
      msg.textContent = s.house.rooms.length > 1 ? `The ${r.name} is empty. Pick up things from another room or shop for more.` : 'Your house is empty. Earn gems in the Academy, then shop for things to fill it.';
    } else if (selected) {
      msg.textContent = `Tap a spot for the ${DECOR_BY_ID[selected].name}, or put it away.`;
    } else {
      msg.textContent = avail.length ? 'Tap a thing, then tap where it goes. Tap a placed thing to move it or put it away.' : 'Tap a placed thing to move it or put it away.';
    }
    // holding something: one tap stores it, it stays hers and waits in the tray
    if (selected) {
      const name = DECOR_BY_ID[selected]?.name ?? 'it';
      tray.append(h('button.btn.small', { style: { borderColor: 'var(--bad)', color: 'var(--bad)' }, onclick: () => { selected = null; renderTray(); draw(); toast(`Put away. The ${name} is waiting in your tray.`); } }, `Put the ${name} away`));
    }
    for (const id of avail) {
      const d = DECOR_BY_ID[id]; if (!d) continue;
      tray.append(h('button.btn.small' + (selected === id ? '.primary' : ''), { onclick: () => { selected = selected === id ? null : id; renderTray(); draw(); } }, d.name));
    }
    if (!selected && r.placed.length) {
      tray.append(h('button.btn.small.ghost', {
        onclick: () => {
          if (pending?.id !== 'clear-room' || Date.now() - pending.t > 6000) { pending = { id: 'clear-room', price: 0, label: 'clear', t: Date.now() }; msg.textContent = `Tap again to put away everything in the ${r.name}.`; return; }
          pending = null;
          update(x => { x.house.rooms[x.house.current].placed = []; });
          toast(`The ${r.name} is empty again. Everything is in your tray.`);
        },
      }, 'Clear this room'));
    }
  }

  function canPlace(d: { w: number; h: number; wall?: boolean }, gx: number, gy: number): boolean {
    if (gx < 0 || gx + d.w > COLS) return false;
    if (d.wall) { if (gy !== 0 && gy !== 1) return false; if (gy + d.h > WALL_ROWS) return false; }
    else { if (gy < WALL_ROWS || gy + d.h > WALL_ROWS + ROWS) return false; }
    // the charm shelf (right end of the wall) and the stairs (left end, living room with a second story) are taken
    if (d.wall && gx + d.w > COLS - 3) return false;
    if (d.wall && room().id === 'living' && houseStories(getSave()) === 2 && gx < 2) return false;
    for (const p of room().placed) {
      const pd = DECOR_BY_ID[p.id]; if (!pd) continue;
      if (gx < p.x + pd.w && gx + d.w > p.x && gy < p.y + pd.h && gy + d.h > p.y) return false;
    }
    return true;
  }

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      const gx = Math.floor(p.x / tile), gy = Math.floor(p.y / tile);
      const r = room();
      // the charm shelf on the wall opens the full collection
      if (!selected && gy < WALL_ROWS && p.x > S.w - tile * 3.4) { go('charms'); return; }
      if (selected) {
        const d = DECOR_BY_ID[selected];
        for (const [ox, oy] of [[0, 0], [-1, 0], [0, -1], [-1, -1], [-2, 0], [0, -2]]) {
          if (canPlace(d, gx + ox, gy + oy)) {
            const id = selected;
            update(s => { s.house.rooms[s.house.current].placed.push({ id, x: gx + ox, y: gy + oy }); });
            selected = null; renderTray(); draw();
            return;
          }
        }
        toast(d.wall ? 'Wall things go on the wall.' : 'That spot is taken or too small.');
        return;
      }
      for (let i = r.placed.length - 1; i >= 0; i--) {
        const it = r.placed[i], d = DECOR_BY_ID[it.id]; if (!d) continue;
        if (gx >= it.x && gx < it.x + d.w && gy >= it.y && gy < it.y + d.h) {
          update(s => { s.house.rooms[s.house.current].placed.splice(i, 1); });
          selected = it.id; renderTray(); draw();
          return;
        }
      }
    },
  });

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (W < 16) return; // not laid out yet
    const s = getSave();
    const r = room();
    tile = W / COLS;
    // wall
    const wall = THEME_BY_ID[r.wall] ?? THEME_BY_ID['wall.cream'];
    wall.draw(c, 0, 0, W, tile * WALL_ROWS, 0, 0);
    c.fillStyle = 'rgba(0,0,0,.12)'; c.fillRect(0, tile * WALL_ROWS - 6, W, 6);
    // floor
    const floor = THEME_BY_ID[r.floor] ?? THEME_BY_ID['floor.wood'];
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) floor.draw(c, x * tile, (WALL_ROWS + y) * tile, tile, tile, x, y);
    // door at the bottom middle
    c.fillStyle = '#8a5a3b'; c.fillRect(W / 2 - tile * 0.6, H - tile * 1.2, tile * 1.2, tile * 1.2);
    // stairs in the living room once there is a second story (decorative, on the wall band)
    if (r.id === 'living' && houseStories(s) === 2) {
      const steps = 5, sw = tile * 1.9 / steps, base = tile * WALL_ROWS - 6;
      for (let k = 0; k < steps; k++) {
        c.fillStyle = k % 2 ? '#a0622d' : '#8a5a3b';
        c.fillRect(tile * 0.1 + k * sw, base - (k + 1) * (tile * 0.3), sw + 1, (k + 1) * (tile * 0.3));
      }
      c.fillStyle = '#5c3a21'; c.fillRect(tile * 0.1 + steps * sw, base - steps * tile * 0.3 - 4, 3, steps * tile * 0.3 + 4);
    }
    // charm shelf on the wall, right side
    const shelfX = W - tile * 3.2, shelfY = tile * 0.55;
    c.fillStyle = '#8a5a3b'; c.fillRect(shelfX, shelfY + tile * 0.5, tile * 3, 6);
    const have = s.charms;
    CHARMS.slice(0, 6).forEach((ch, i) => {
      const x = shelfX + tile * 0.3 + i * tile * 0.48, y = shelfY + tile * 0.3;
      if (have.includes(ch.id)) drawCharm(c, ch, x, y, tile * 0.18);
      else { c.fillStyle = 'rgba(0,0,0,.08)'; c.beginPath(); c.arc(x, y, tile * 0.14, 0, 7); c.fill(); }
    });
    if (have.length > 6) { c.fillStyle = '#2a2a2e'; c.font = `800 ${Math.round(tile * 0.3)}px Nunito, sans-serif`; c.textAlign = 'left'; c.fillText(`+${have.length - 6}`, shelfX + tile * 3.1, shelfY + tile * 0.4); }
    // placed items (sorted by y so lower things draw in front)
    const sorted = r.placed.slice().sort((a, b) => a.y - b.y);
    for (const p of sorted) {
      const d = DECOR_BY_ID[p.id]; if (!d) continue;
      if (!drawArtContain(c, d.id, p.x * tile, p.y * tile, d.w * tile, d.h * tile)) d.draw(c, p.x * tile, p.y * tile, d.w * tile, d.h * tile);
    }
    // selection ghost
    if (selected) {
      const d = DECOR_BY_ID[selected];
      c.fillStyle = 'rgba(59,179,169,.18)';
      if (d.wall) c.fillRect(0, 0, W, tile * WALL_ROWS); else c.fillRect(0, tile * WALL_ROWS, W, tile * ROWS);
    }
    // room name plaque for rooms beyond the first
    if (s.house.rooms.length > 1) {
      c.fillStyle = 'rgba(42,42,46,.75)'; c.fillRect(8, 8, Math.min(W * 0.5, 170), 24);
      c.fillStyle = '#f6f3ec'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'middle';
      c.fillText(r.name, 16, 20);
    }
    // avatar by the door, pet alongside
    drawSprite(c, s.avatar, W / 2, H - tile * 0.25, Math.max(2, tile / 16), 0, 'up');
    const pet = activePet(s);
    if (pet) drawPet(c, pet, W / 2 + tile * 1.2, H - tile * 0.25, Math.max(2, tile / 16));
    void ROOM_DEF_BY_ID; void hasRoom;
  }

  // the garden strip: plots she grew in Multiplication Garden
  const G = makeSurface(gardenStage, { height: 92, draw: false, onResize: drawGarden });
  function drawGarden() {
    const c = G.ctx, W = G.w, H = G.h;
    const plots = (getSave().kept.plot || []) as { t: number; data: { rows: number; cols: number } }[];
    c.fillStyle = '#cfe6b3'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'top';
    c.fillText(plots.length ? `Garden: ${plots.length} plot${plots.length === 1 ? '' : 's'} grown` : 'Garden: grow plots in Multiplication Garden', 10, 6);
    const shown = plots.slice(-6);
    const slot = (W - 20) / 6, s = Math.min(slot - 8, 56);
    shown.forEach((p, i) => {
      const { rows, cols } = p.data;
      const cell = s / Math.max(rows, cols, 1);
      const pw = cell * cols, ph = cell * rows;
      const x = 10 + i * slot + (slot - pw) / 2, y = 26 + (s - ph) / 2;
      c.fillStyle = '#8a5a3b'; c.fillRect(x - 2, y - 2, pw + 4, ph + 4);
      for (let r = 0; r < rows; r++) for (let k = 0; k < cols; k++) { c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(x + k * cell + cell / 2, y + r * cell + cell / 2, Math.max(1.5, cell * 0.3), 0, 7); c.fill(); }
    });
  }

  renderPlan(); renderTray(); renderPaint(); draw();
  const un = subscribe(() => { renderPlan(); renderTray(); renderPaint(); draw(); drawGarden(); });
  const unArt = onArtReady(draw);
  return () => { unPointer(); S.destroy(); G.destroy(); un(); unArt(); };
}
